import { useEffect, useState } from 'react';

/**
 * Tracks the application's light/dark mode.
 *
 * `src/components/shared/theme-toggle` is the owner of that state: it sets
 * `data-theme="dark"` on `<html>` and removes the attribute for light mode.
 * Reading the attribute rather than `localStorage` keeps the widget in sync even
 * when the toggle changes the theme after mount.
 */

function read(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

export function useAppTheme(): 'light' | 'dark' {
  const [theme, setTheme] = useState<'light' | 'dark'>(read);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const sync = () => setTheme(read());

    // The attribute can change at any time, so observe it rather than polling.
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    // Catch a toggle that happened between first render and this effect.
    sync();

    return () => observer.disconnect();
  }, []);

  return theme;
}
